import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FloppyDisk, Trash } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const MODULE_LABELS = {
  dashboard: 'Tableau de bord',
  clients: 'Clients',
  agents: 'Agents',
  assignments: 'Affectations',
  supervisors: 'Superviseurs',
  sites_map: 'Carte des Sites',
  projects: 'Projets',
  calendar: 'Calendrier',
  alerts_calendar: 'Calendrier Alertes',
  reports: 'Rapports',
  logistics: 'Logistique',
  finance: 'Finance',
  messages: 'Messages',
  trash: 'Corbeille',
  admin: 'Administration',
};

const emptyMatrix = (mods) => Object.fromEntries(mods.map((m) => [m, { read: false, write: false }]));

const UserPermissionsDialog = ({ open, onOpenChange, user }) => {
  const [modules, setModules] = useState([]);
  const [perms, setPerms] = useState({});
  const [templates, setTemplates] = useState([]);
  const [selectedTpl, setSelectedTpl] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    (async () => {
      const [modRes, permRes, tplRes] = await Promise.all([
        fetch(`${API}/api/admin/modules`, { credentials: 'include' }),
        fetch(`${API}/api/admin/users/${user.user_id}/permissions`, { credentials: 'include' }),
        fetch(`${API}/api/admin/permission-templates`, { credentials: 'include' }),
      ]);
      const modData = modRes.ok ? await modRes.json() : { modules: [] };
      const permData = permRes.ok ? await permRes.json() : { permissions: {} };
      const tplData = tplRes.ok ? await tplRes.json() : [];
      setModules(modData.modules || []);
      const base = emptyMatrix(modData.modules || []);
      setPerms({ ...base, ...(permData.permissions || {}) });
      setTemplates(tplData);
      setSelectedTpl('');
      setTemplateName('');
    })();
  }, [open, user]);

  const toggle = (mod, key) => {
    setPerms((p) => {
      const cur = p[mod] || { read: false, write: false };
      const next = { ...cur, [key]: !cur[key] };
      // Cocher "modification" force la lecture
      if (key === 'write' && next.write) next.read = true;
      // Décocher "lecture" retire aussi la modification
      if (key === 'read' && !next.read) next.write = false;
      return { ...p, [mod]: next };
    });
  };

  const applyTemplate = (tplId) => {
    setSelectedTpl(tplId);
    const tpl = templates.find((t) => t.template_id === tplId);
    if (tpl) {
      setPerms({ ...emptyMatrix(modules), ...(tpl.permissions || {}) });
      toast.success(`Profil « ${tpl.name} » appliqué`);
    }
  };

  const clearAll = () => setPerms(emptyMatrix(modules));

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/admin/users/${user.user_id}/permissions`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permissions: perms }),
      });
      if (r.ok) { toast.success('Permissions enregistrées'); onOpenChange(false); }
      else toast.error('Erreur d\'enregistrement');
    } catch { toast.error('Erreur réseau'); }
    setSaving(false);
  };

  const saveAsTemplate = async () => {
    const name = templateName.trim();
    if (!name) { toast.error('Nom requis'); return; }
    try {
      const r = await fetch(`${API}/api/admin/permission-templates`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, permissions: perms }),
      });
      if (r.ok) {
        const tpl = await r.json();
        setTemplates((ts) => [...ts, tpl]);
        setTemplateName('');
        toast.success(`Profil « ${name} » enregistré`);
      } else toast.error('Erreur d\'enregistrement');
    } catch { toast.error('Erreur réseau'); }
  };

  const deleteTemplate = async () => {
    if (!selectedTpl) return;
    if (!window.confirm('Supprimer ce profil type ?')) return;
    try {
      const r = await fetch(`${API}/api/admin/permission-templates/${selectedTpl}`, { method: 'DELETE', credentials: 'include' });
      if (r.ok) {
        setTemplates((ts) => ts.filter((t) => t.template_id !== selectedTpl));
        setSelectedTpl('');
        toast.success('Profil supprimé');
      }
    } catch { toast.error('Erreur'); }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Permissions — {user.name}</DialogTitle>
          <DialogDescription className="text-[#94A3B8]">
            Cocher active une permission personnalisée qui prime sur le rôle « {user.role} ». Tout laisser décoché = permissions du rôle uniquement.
          </DialogDescription>
        </DialogHeader>

        {/* Barre profils types */}
        <div className="flex flex-wrap items-center gap-2 p-3 rounded-md bg-[#1E293B]/50 border border-[#1E293B]">
          <div className="flex-1 min-w-[180px]">
            <Label className="text-xs text-[#94A3B8]">Charger un profil type</Label>
            <Select value={selectedTpl} onValueChange={applyTemplate}>
              <SelectTrigger className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="perm-template-select">
                <SelectValue placeholder={templates.length ? 'Choisir un profil...' : 'Aucun profil enregistré'} />
              </SelectTrigger>
              <SelectContent className="bg-[#0F172A] border-[#1E293B]">
                {templates.map((t) => (
                  <SelectItem key={t.template_id} value={t.template_id} className="text-[#F8FAFC]">{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selectedTpl && (
            <Button variant="outline" size="sm" onClick={deleteTemplate} className="border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 self-end"><Trash size={14} className="mr-1" /> Supprimer profil</Button>
          )}
          <Button variant="outline" size="sm" onClick={clearAll} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B] self-end" data-testid="perm-clear-all">Tout décocher</Button>
        </div>

        {/* Matrice */}
        <div className="overflow-hidden rounded-md border border-[#1E293B]">
          <table className="w-full text-sm">
            <thead className="bg-[#1E293B] text-[#94A3B8] text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Module</th>
                <th className="px-3 py-2 text-center w-24">Lecture</th>
                <th className="px-3 py-2 text-center w-32">Modification</th>
              </tr>
            </thead>
            <tbody>
              {modules.map((m) => (
                <tr key={m} className="border-t border-[#1E293B]">
                  <td className="px-3 py-2 text-[#F8FAFC]">{MODULE_LABELS[m] || m}</td>
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={!!perms[m]?.read}
                      onChange={() => toggle(m, 'read')}
                      className="h-4 w-4 accent-[#3B82F6] cursor-pointer"
                      data-testid={`perm-${m}-read`}
                    />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={!!perms[m]?.write}
                      onChange={() => toggle(m, 'write')}
                      className="h-4 w-4 accent-[#10B981] cursor-pointer"
                      data-testid={`perm-${m}-write`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Enregistrer comme profil type */}
        <div className="flex items-end gap-2 p-3 rounded-md bg-[#1E293B]/30 border border-[#1E293B]">
          <div className="flex-1">
            <Label className="text-xs text-[#94A3B8]">Enregistrer les permissions actuelles comme profil type</Label>
            <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="Ex : Agent terrain" className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="perm-tpl-name" />
          </div>
          <Button variant="outline" onClick={saveAsTemplate} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]" data-testid="perm-save-template"><FloppyDisk size={16} className="mr-1" /> Créer profil</Button>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]">Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="perm-save-btn">
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserPermissionsDialog;
